import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Product } from "./../../cubiq-model-multi-asset/model/enum/Product";

import { BfxColDef, TableDefinition } from "./TableDefinition";

import { RateCache } from "../rate/impl/RateCache";

import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";

import { SystemConfigPromiseService } from "shared/auth-services";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { LoggerService } from "@bfx/logger";
import { OrdersTableDefinitionFactory } from "./OrdersTableDefinitionFactory";
import OrdersTableDefinitionFactoryConstants, {
  COLUMN_AGGREGATION_KEY,
  COLUMN_AGGREGATION_PARENT_ID,
  COLUMN_BASE_PERCENTAGE,
  COLUMN_BASE_SPOT_MARKET_MID,
  COLUMN_DEALT_PERCENTAGE,
  COLUMN_DESK_ID,
  COLUMN_ORDER_ACTIVATION_DATE,
  COLUMN_ORDER_AMOUNT,
  COLUMN_ORDER_BASE_FWD_PTS,
  COLUMN_ORDER_DEALT_FWD_PTS,
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_ORDER_FIXING_SOURCE_CODE,
  COLUMN_ORDER_FWD_BASE_TRACKING,
  COLUMN_ORDER_FWD_DEALT_TRACKING,
  COLUMN_ORDER_NDF_BASE_FWD_PTS,
  COLUMN_ORDER_NDF_BASE_TRACKING,
  COLUMN_ORDER_NDF_DEALT_FWD_PTS,
  COLUMN_ORDER_NDF_DEALT_TRACKING,
  COLUMN_SPOT_MARKET_MID,
  COLUMN_TARGET_DESK_ID,
  COLUMN_TRADER_GROUP_NAMES,
  COLUMN_CANCEL_DTS,
  COLUMN_CANCEL_USER_ID,
} from "./OrdersTableDefinitionFactory.constants";

export const COLUMN_ORDER_MODIFY_DATE = "modifyDts"; // from T_LIMIT_ORDER_REV.UPDATE_STAMP
const logger = LoggerService.for(__dirname);

import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { RequestOrderType } from "shared/cubiq-model-multi-asset";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { BmarkTraderGroupNameHelper } from "./benchmarks/helpers/BmarkTradeGroupNameHelper";
import { OrdersTableMode } from "./OrdersTableMode";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { FIND_ORDERS_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { ColumnFilterType } from "./ColumnFilterType";
import {
  FIXING_TIME_UTC_VALUE_FORMATTER,
  FIXING_TIME_UTC_VALUE_GETTER,
  FIXING_TIME_VALUE_GETTER,
} from "./BmarkHedgeOrdersTableDefinitionFactory.constants";
import { IndexType } from "./IndexType";
import { EMPTY_AMT_RATE_VALUE } from "shared/cubiq-services/model/BaseTableDefinitionFactory";

const EMPTY_TEXT_VALUE: string = "";
export const HIDE_IN_FIND: Array<string> = [
  COLUMN_DEALT_PERCENTAGE,
  COLUMN_SPOT_MARKET_MID,
  COLUMN_BASE_SPOT_MARKET_MID,
  COLUMN_AGGREGATION_PARENT_ID,
  COLUMN_AGGREGATION_KEY,
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_TARGET_DESK_ID,
  COLUMN_ORDER_DEALT_FWD_PTS,
  COLUMN_ORDER_FWD_DEALT_TRACKING,
  COLUMN_ORDER_BASE_FWD_PTS,
  COLUMN_ORDER_FWD_BASE_TRACKING,

  COLUMN_ORDER_NDF_DEALT_TRACKING,
  COLUMN_ORDER_NDF_BASE_TRACKING,
  COLUMN_ORDER_NDF_DEALT_FWD_PTS,
  COLUMN_ORDER_NDF_BASE_FWD_PTS,
  COLUMN_DEALT_PERCENTAGE,
  COLUMN_BASE_PERCENTAGE,
  COLUMN_SPOT_MARKET_MID,
  COLUMN_BASE_SPOT_MARKET_MID,
];

const CANCEL_DATE_SORT_KEY = "cancelDateSort";
@Injectable()
export class FindOrdersTableDefinitionFactory extends OrdersTableDefinitionFactory {
  private myBmarkTradeGroupNameHelper: BmarkTraderGroupNameHelper;

  static isBmarkOrder(requestedOrderType: RequestOrderType) {
    return (
      requestedOrderType === RequestOrderType.BMARK ||
      requestedOrderType === RequestOrderType.BMALGO
    );
  }

  constructor(
    profileService: ProfilePromiseActionService,
    rateCache: RateCache,
    protected translationService: TranslateService,
    @Optional() rateSourceLookup: RateSourceLookupService,
    @Optional() myClientLookup: ClientLookupService,
    @Optional() myAccountLookup: AccountLookupService,
    @Optional() mySalesGroupLookup: SalesGroupLookupService,
    @Optional() myTraderGroupLookup: TraderGroupLookupService,
    @Optional() ccyLookupService: CcyLookupService,
    @Optional() ccyPairLookupService: CcyPairLookupService,
    @Optional() userLookupService: UserLookupService,
    @Optional() sysConfigService: SystemConfigPromiseService,
    @Optional() translateService: TranslateService,
    @Optional() myBasePricingService: BasePricingService,
    @Optional() myDealtPricingService: DealtPricingService,
    @Optional() fixingSourceLookupService: FixingSourceLookupService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional()
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() atdlTooltipUiRep: AtdlTooltipUiRep,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      rateCache,
      rateSourceLookup,
      myClientLookup,
      myAccountLookup,
      mySalesGroupLookup,
      myTraderGroupLookup,
      ccyLookupService,
      ccyPairLookupService,
      userLookupService,
      sysConfigService,
      translateService,
      myBasePricingService,
      myDealtPricingService,
      fixingSourceLookupService,
      algoStrategyLookupService,
      currencyPairCalendarLookupService,
      cmp,
      amp,
      ctmp,
      atdlTooltipUiRep,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );

    this.mode = OrdersTableMode.DEALT;
    this.myBmarkTradeGroupNameHelper = new BmarkTraderGroupNameHelper(
      myTraderGroupLookup,
      COLUMN_DESK_ID,
    );
    this.myStatusIconHelper.showOnlyStrategyIcons = true;
    this.enabledVolumePerMinute = false;
    this.enabledPortfolio = true;
  }

  public createDef(): TableDefinition {
    let td = super.createDef();

    let position = this.position;
    // td.columns[td.columnIndexMap.get("selection")].adapter = new SelectionAdapter();
    td.columns[td.columnIndexMap.get("selection")].width = 76;
    td.columns[td.columnIndexMap.get("selection")].minWidth = this.threeWs;
    td.columns[td.columnIndexMap.get("selection")].maxWidth = 76;

    td.columns[td.columnIndexMap.get(COLUMN_DESK_ID)].valueGetter = ((
      params: ValueGetterParams,
    ) => {
      if (params.data.requestedOrderType == RequestOrderType.BMARK) {
        return params.data[COLUMN_TRADER_GROUP_NAMES];
      }
      let traderGroupData = this.myTraderGroupLookup.getTraderGroup(params.data[COLUMN_DESK_ID]);
      if (traderGroupData) {
        return traderGroupData.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }).bind(this);

    if (td.columns[td.columnIndexMap.get(COLUMN_ORDER_FIXING_SOURCE_CODE)] != null) {
      td.columns[td.columnIndexMap.get(COLUMN_ORDER_FIXING_SOURCE_CODE)].valueGetter = ((
        params: ValueGetterParams,
      ) => {
        let isNDFOrder = params.data.product == Product.NDF;
        let fixingCodeValue = params.data[COLUMN_ORDER_FIXING_SOURCE_CODE];
        if (isNDFOrder && fixingCodeValue == null) {
          return this.translationService.instant("fixingSource.custom");
        }
        return fixingCodeValue;
      }).bind(this);
    }

    td.columns[td.columnIndexMap.get(COLUMN_DESK_ID)].minWidth = this.oneWs;
    td.columns[td.columnIndexMap.get(COLUMN_DESK_ID)].width = this.sevenWs;

    let fixingTimeColumn: BfxColDef = this.initColdDef();

    fixingTimeColumn.headerName = "Fixing Time";
    fixingTimeColumn.field = "reqFixingTime";
    fixingTimeColumn.columnFilterType = ColumnFilterType.NONE;
    fixingTimeColumn.minWidth = this.oneWs;
    fixingTimeColumn.width = this.fiveWs;
    fixingTimeColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeColumn.valueGetter = FIXING_TIME_VALUE_GETTER.bind(this);
    if (!this.restoreFromConfig(fixingTimeColumn)) {
      fixingTimeColumn.hide = true;
      fixingTimeColumn.position = position.next();
    }

    td.addColumn(fixingTimeColumn);

    /**
     * Fixing Time in UTC terms column
     */
    let fixingTimeUTCColumn: BfxColDef = this.initColdDef();

    fixingTimeUTCColumn.headerName = "Fixing UTC Time";
    fixingTimeUTCColumn.field = "fixingTimeUTC";
    fixingTimeUTCColumn.headerTooltip = "Fixing Time in UTC Terms";
    fixingTimeUTCColumn.minWidth = this.oneWs;
    fixingTimeUTCColumn.width = this.eightWs;
    fixingTimeUTCColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeUTCColumn.indexType = IndexType.String;
    fixingTimeUTCColumn.columnFilterType = ColumnFilterType.NONE;

    fixingTimeUTCColumn.valueGetter = FIXING_TIME_UTC_VALUE_GETTER.bind(this);
    fixingTimeUTCColumn.valueFormatter = FIXING_TIME_UTC_VALUE_FORMATTER.bind(this);

    if (!this.restoreFromConfig(fixingTimeUTCColumn)) {
      fixingTimeUTCColumn.hide = true;
      fixingTimeUTCColumn.position = position.next();
    }
    td.addColumn(fixingTimeUTCColumn);

    this.addAlgoSpecificColumns(td);

    // For Find Orders, we want to only export Good From column if user has explicitly made it visble
    const selectionCol = td.columns[td.columnIndexMap.get("selection")];
    const goodFromCol = td.columns[td.columnIndexMap.get(COLUMN_ORDER_ACTIVATION_DATE)];
    if (selectionCol && goodFromCol) {
      selectionCol.skipExport = true;
      goodFromCol.skipExport = false;
    }

    /* Column For Forward First (quote ID) */
    let reqQuoteIdCol: BfxColDef = this.initColdDef();
    reqQuoteIdCol.headerName = "Forward First";
    reqQuoteIdCol.field = "reqQuoteId";
    reqQuoteIdCol.columnFilterType = ColumnFilterType.NONE;
    reqQuoteIdCol.minWidth = this.oneWs;
    reqQuoteIdCol.width = this.fiveWs;
    reqQuoteIdCol.valueFormatter = (params: ValueFormatterParams) => {
      return params.value ?? "-";
    };
    if (!this.restoreFromConfig(reqQuoteIdCol)) {
      reqQuoteIdCol.hide = true;
      reqQuoteIdCol.position = position.next();
    }
    td.addColumn(reqQuoteIdCol);

    this.addCancelColumns(td);
    // TODO - NB - **
    // change to value getter
    // td.columns[td.columnIndexMap.get(COLUMN_VALUE_DATE)].adapter = new TenorAdapter();
    HIDE_IN_FIND.forEach((colid) => {
      this.removeColumnFromDef(colid, td);
    });

    return td;
  }
  addCancelColumns(td: TableDefinition) {
    td.addColumn(this.createCancelUserColumn("Cancel User"));
    td.addColumn(this.createCancelDateColumn("Cancel Date"));
  }
  createCancelDateColumn(title: string): BfxColDef {
    let cancelDateCol: BfxColDef = this.initColdDef();
    cancelDateCol.headerName = title;
    cancelDateCol.field = COLUMN_CANCEL_DTS;
    cancelDateCol.minWidth = this.oneWs;
    cancelDateCol.width = this.sixWs;
    cancelDateCol.columnFilterType = ColumnFilterType.NONE;
    cancelDateCol.valueFormatter = this.formatMomment.bind(this);
    if (!this.restoreFromConfig(cancelDateCol)) {
      cancelDateCol.hide = true;
      cancelDateCol.position = this.position.next();
    }

    return cancelDateCol;
  }

  createCancelUserColumn(title: string): BfxColDef {
    let cancelUserCol: BfxColDef = this.initColdDef();
    cancelUserCol.headerName = title;
    cancelUserCol.field = COLUMN_CANCEL_USER_ID;
    cancelUserCol.minWidth = this.oneWs;
    cancelUserCol.width = this.sixWs;
    cancelUserCol.columnFilterType = ColumnFilterType.USER;
    cancelUserCol.valueGetter = function (this: FindOrdersTableDefinitionFactory, params) {
      let user = this.userLookupService.getItem(params.data[COLUMN_CANCEL_USER_ID]);
      if (user) {
        return user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    cancelUserCol.valueFormatter = function (this: FindOrdersTableDefinitionFactory, params) {
      let user = this.userLookupService.getItem(params.data[COLUMN_CANCEL_USER_ID]);
      if (user) {
        return this.showUserName ? user.userName : user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    if (!this.restoreFromConfig(cancelUserCol)) {
      cancelUserCol.position = this.position.next();
    }

    return cancelUserCol;
  }

  removeColumnFromDef(id: string, td: TableDefinition): void {
    const index = td.columnIndexMap.get(id);
    if (index > -1) {
      td.columns.splice(index, 1);
    }

    td.columnIndexMap.forEach((value: number, key: string) => {
      if (value > index) {
        td.columnIndexMap.set(key, value - 1);
      }
    });
  }

  public getVersionIdentifier(): string {
    return FIND_ORDERS_TABLE_DEFINITION_VER;
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
    this.mode = OrdersTableMode.DEALT;
  }

  /**
   * Format an amount using the correct precision of the currencies minor units precision.
   * @param currency
   * @param amt
   * @returns
   */
  protected override formatCcyAmount(currency: string, amt: CubiqTS.BigNumber) {
    let result: string = null;

    if (amt && currency) {
      const ccy = this.ccyLookupService.getCcy(currency);
      let precision: number = 0;

      if (ccy != null && ccy.minorUnitsQty != null) {
        precision = ccy.minorUnitsQty.toNumber();
      }
      result = amt.toFormat(precision, CubiqTS.BigNumber.ROUND_HALF_CEIL);
    }

    if (result == null) {
      result = EMPTY_AMT_RATE_VALUE;
    }

    return result;
  }
}
